import {
  SITE_DESCRIPTION,
  SITE_NAME,
  SITE_PAGES,
  SITE_URL,
  SOCIAL_IMAGE_PATH,
  SOCIAL_PROFILE_URLS,
  SUPPORT_EMAIL,
  absoluteUrl,
  type SitePageKey
} from './site';
import type { FaqItem } from './faq-content';

const ORGANIZATION_ID = `${SITE_URL}/#organization`;
const WEBSITE_ID = `${SITE_URL}/#website`;

export function organizationSchema() {
  return {
    '@type': 'Organization' as const,
    '@id': ORGANIZATION_ID,
    name: SITE_NAME,
    url: SITE_URL,
    logo: absoluteUrl('/brand/edvello-icon-512.png'),
    image: absoluteUrl(SOCIAL_IMAGE_PATH),
    description: SITE_DESCRIPTION,
    email: SUPPORT_EMAIL,
    ...(SOCIAL_PROFILE_URLS.length > 0 ? { sameAs: SOCIAL_PROFILE_URLS } : {}),
    contactPoint: [
      {
        '@type': 'ContactPoint' as const,
        contactType: 'customer support',
        email: SUPPORT_EMAIL,
        url: absoluteUrl(SITE_PAGES.contact.path),
        availableLanguage: ['English']
      }
    ]
  };
}

export function websiteSchema() {
  return {
    '@type': 'WebSite' as const,
    '@id': WEBSITE_ID,
    name: SITE_NAME,
    url: SITE_URL,
    description: SITE_DESCRIPTION,
    inLanguage: 'en-IN',
    publisher: { '@id': ORGANIZATION_ID }
  };
}

export function softwareSchema() {
  return {
    '@type': 'SoftwareApplication' as const,
    name: SITE_NAME,
    url: SITE_URL,
    applicationCategory: 'EducationalApplication',
    applicationSubCategory: 'Coaching institute and tuition management software',
    operatingSystem: 'Web browser on desktop and mobile',
    description: SITE_DESCRIPTION,
    areaServed: 'IN',
    publisher: { '@id': ORGANIZATION_ID },
    featureList: [
      'Learning management system with video and PDF lessons',
      'Scheduled live classes with joining links',
      'Batches, teacher assignment and student enrollment',
      'Recurring timetables and manual attendance',
      'Exercises, submissions, grading and feedback',
      'Community discussions linked to courses',
      'Branded institution website and course catalogue',
      'Role-based access for administrators, teachers, students and guardians'
    ],
    audience: {
      '@type': 'EducationalAudience' as const,
      educationalRole: 'tuition teachers, institutions and schools'
    }
  };
}

export function faqSchema(items: FaqItem[]) {
  return {
    '@type': 'FAQPage' as const,
    mainEntity: items.map((item) => ({
      '@type': 'Question' as const,
      name: item.question,
      acceptedAnswer: { '@type': 'Answer' as const, text: item.answer }
    }))
  };
}

export function webPageSchema(key: SitePageKey, type: 'WebPage' | 'ContactPage' = 'WebPage') {
  const page = SITE_PAGES[key];

  return {
    '@type': type,
    '@id': `${absoluteUrl(page.path)}#webpage`,
    url: absoluteUrl(page.path),
    name: page.title,
    description: page.description,
    inLanguage: 'en-IN',
    primaryImageOfPage: { '@type': 'ImageObject' as const, url: absoluteUrl(SOCIAL_IMAGE_PATH) },
    isPartOf: { '@id': WEBSITE_ID },
    about: { '@id': ORGANIZATION_ID }
  };
}

export function breadcrumbSchema(key: SitePageKey, label: string) {
  return {
    '@type': 'BreadcrumbList' as const,
    itemListElement: [
      { '@type': 'ListItem' as const, position: 1, name: SITE_NAME, item: SITE_URL },
      { '@type': 'ListItem' as const, position: 2, name: label, item: absoluteUrl(SITE_PAGES[key].path) }
    ]
  };
}
